import React, { useState, SyntheticEvent, useRef, FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { Search as SearchIcon } from '@fenderdigital/ui/Icons';
import { useEscapeKey } from '@fenderdigital/custom-hooks';
import ScreenReaderOnly from '@fenderdigital/ui/ScreenReaderOnly';
import useMountTransition from '@fenderdigital/custom-hooks/hooks/useMountTransition';
import useInstantSearch from '../../hooks/useInstantSearch';
import SearchResults from './SearchResults';
import { RootState } from '../../redux-types/root.redux-types';
import { useSearchSelection } from '../../contexts/SearchSelectionContext';
import * as S from './NavSearch-styled';

interface NavSearchProps {
  isTransparent?: boolean;
}

const selector = (state: RootState): Record<string, any> => {
  const { myPath, onboarding } = state;
  const { instrument: pathInstrument } = myPath ?? {};
  const { instrument: onboardingInstrument } = onboarding ?? {};
  const instrument = pathInstrument ?? onboardingInstrument;
  return { instrument };
};

const NavSearch: FC<NavSearchProps> = ({ isTransparent }) => {
  const { instrument } = useSelector(selector);
  const { selectedOptionId } = useSearchSelection();
  const [selectedInstrument, setSelectedInstrument] = useState(instrument ?? 'guitar');
  const isTabletUp = useBreakpoint(up('tablet-portrait'));
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState<string>('');
  const { isLoading, results, facets } = useInstantSearch(query, selectedInstrument) ?? {};
  const hasTransitionedIn = useMountTransition(showSearch);
  const mobileInputRef = useRef<any>();
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchButtonRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const handleChange = (event: SyntheticEvent): void => {
    const { value } = event.target as HTMLInputElement;
    setQuery(value);
  };
  const handleClose = (): void => setQuery('');
  const mobileInputFocus = (): any => setTimeout(() => mobileInputRef?.current?.focus(), 0);
  const mobileButtonFocus = (): any => setTimeout(() => mobileSearchButtonRef?.current?.focus(), 0);
  const handleShowSearch = (): void => {
    setShowSearch(true);
  };
  const handleCloseSearch = (): void => {
    setShowSearch(false);
    setQuery('');
    if (isTabletUp) {
      desktopInputFocus();
    } else {
      mobileButtonFocus();
    }
  };

  const handleCategoryChange = (instrumentType: string): void => {
    setSelectedInstrument(instrumentType);
  };

  const handleSubmit = (e: any): void => {
    e.preventDefault();
    history.push(`/search/results?query=${query}`);
    handleClose();
  };

  const resultsRef = useEscapeKey<any>(handleCloseSearch);
  const desktopInputFocus = (): void => resultsRef.current.focus();

  // handles focus for mobile search input
  useEffect(() => {
    if (hasTransitionedIn && !isTabletUp) mobileInputFocus();
  }, [hasTransitionedIn, isTabletUp]);

  // closes search results if clicked outside component
  useEffect(() => {
    const checkIfClickedOutside = (event: MouseEvent): void => {
      if (query && searchRef.current && !searchRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mouseup', checkIfClickedOutside);
    return (): void => {
      document.removeEventListener('mouseup', checkIfClickedOutside);
    };
  }, [query]);

  // closes search results if tabbed out of search component
  useEffect(() => {
    const closeModal = (): void => {
      if (
        document.activeElement?.getAttribute('data-id') === 'streaks-button' ||
        document.activeElement?.getAttribute('href') === '/play/'
      ) {
        handleClose();
        setShowSearch(false);
      }
    };
    document.addEventListener('focusin', closeModal);
    return (): void => document.removeEventListener('focusin', closeModal);
  }, []);

  return isTabletUp ? (
    <S.SearchContainer ref={searchRef}>
      <S.SearchForm onSubmit={handleSubmit}>
        <label htmlFor="search">
          <ScreenReaderOnly>Search</ScreenReaderOnly>
          <SearchIcon
            data-id="app-bar-icon-search"
            fill={isTransparent ? 'white' : 'black'}
            fontSize="1rem"
            aria-hidden
            css={`
              position: absolute;
              top: 0.5rem;
              left: 1rem;
            `}
          />
        </label>
        <S.Input
          isTransparent={isTransparent}
          id="search"
          type="text"
          placeholder="Search by songs, skills & more"
          value={query}
          data-id="app-bar-search-field"
          onChange={handleChange}
          ref={resultsRef}
          aria-autocomplete="list"
          aria-controls="nav-search-results"
          aria-haspopup="listbox"
          aria-expanded={!!query}
          aria-describedby="search-instructions"
          aria-activedescendant={selectedOptionId}
        />
      </S.SearchForm>
      {query && (
        <>
          <SearchResults
            results={results}
            isLoading={isLoading}
            query={query}
            onClose={handleClose}
            facets={facets}
            instrument={selectedInstrument}
            onCategoryChange={handleCategoryChange}
          />
          <S.CloseButton
            isTransparent={isTransparent}
            onClick={handleCloseSearch}
            aria-label="Clear search"
          >
            <S.CloseIcon isTransparent={isTransparent} aria-hidden />
          </S.CloseButton>
        </>
      )}
      <ScreenReaderOnly id="search-instructions">
        Begin typing to search, use arrow keys to navigate, Enter to select.
      </ScreenReaderOnly>
    </S.SearchContainer>
  ) : (
    <>
      {showSearch && (
        <S.InputContainer id="mobile-search-container" ref={resultsRef} onSubmit={handleSubmit}>
          <label htmlFor="search">
            <ScreenReaderOnly>Search</ScreenReaderOnly>
          </label>
          <S.MobileInput
            value={query}
            onChange={handleChange}
            id="search"
            type="text"
            placeholder="Search by songs, skills & more"
            ref={mobileInputRef}
            data-id="mobile-search-input"
            aria-autocomplete="list"
            aria-controls="nav-search-results"
            aria-haspopup="listbox"
            aria-expanded={!!query}
            aria-describedby="search-instructions"
            aria-activedescendant={selectedOptionId}
          />
          <ScreenReaderOnly id="search-instructions">
            Begin typing to search, use arrow keys to navigate, Enter to select.
          </ScreenReaderOnly>
        </S.InputContainer>
      )}
      <S.MobileSearchContainer>
        <S.MobileSearchWrapper showSearch={showSearch}>
          <S.MobileSearchButton
            as={showSearch ? 'div' : 'button'}
            onClick={handleShowSearch}
            aria-hidden={showSearch}
            aria-label="search"
            showSearch={showSearch}
            ref={mobileSearchButtonRef}
          >
            <S.MobileSearchIcon data-id="app-bar-icon-search" fontSize="1.125rem" aria-hidden />
          </S.MobileSearchButton>
        </S.MobileSearchWrapper>
      </S.MobileSearchContainer>
      {query && (
        <>
          <SearchResults
            results={results}
            isLoading={isLoading}
            query={query}
            onClose={handleCloseSearch}
            facets={facets}
            instrument={selectedInstrument}
            onCategoryChange={handleCategoryChange}
          />
        </>
      )}
      {showSearch && (
        <S.CloseButton onClick={handleCloseSearch} aria-label="Close search" isMobile>
          <S.CloseIcon aria-hidden style={{ fill: 'black' }} />
        </S.CloseButton>
      )}
    </>
  );
};

export default NavSearch;
